import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Briefcase, CalendarClock, CheckCircle2, Download, FileSpreadsheet, FileText, Handshake, Loader2, PiggyBank, Users, Wallet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MetricCard } from '@/components/reports/MetricCard';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import {
  describeFeeRule,
  usePartnerStatementLeads,
  useReferralPartnerTotals,
  useReferralPartners,
  useReferralPayouts,
  type ReferralPartner,
} from '@/hooks/useReferralPartners';
import {
  buildPartnerStatement,
  feeStatusLabel,
  generatePartnerStatementHtml,
  isPercentBasis,
  outcomeText,
  partnerStatementCsv,
  shareIncTax,
  showsCosts,
} from '@/utils/partnerStatement';
import { downloadHtmlReport } from '@/utils/techCommissionHtmlGenerator';
import { cn } from '@/lib/utils';

interface ReferralsReportProps {
  startDate: Date;
  endDate: Date;
  timezone: string;
  compareEnabled?: boolean;
}

/**
 * Referral partners: who sends us work, what came of it in the period, and what
 * they are owed. The per-partner section is the same statement the office
 * downloads and sends from Settings, run over the report's date range.
 */
export function ReferralsReport({ startDate, endDate }: ReferralsReportProps) {
  const { formatMoney, formatShortDate } = useCompanyLocale();
  const { data: partners = [], isLoading: partnersLoading } = useReferralPartners();
  const { data: totals = {} } = useReferralPartnerTotals();
  const [searchParams, setSearchParams] = useSearchParams();

  // ?partner=<id> keeps the pick across reloads and makes the tab linkable.
  const requested = searchParams.get('partner');
  const selected: ReferralPartner | null = useMemo(() => {
    if (!partners.length) return null;
    return partners.find((p) => p.id === requested) ?? partners.find((p) => p.is_active) ?? partners[0];
  }, [partners, requested]);

  useEffect(() => {
    if (selected && requested !== selected.id) {
      const next = new URLSearchParams(searchParams);
      next.set('partner', selected.id);
      setSearchParams(next, { replace: true });
    }
  }, [selected, requested, searchParams, setSearchParams]);

  const pick = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('partner', id);
    setSearchParams(next, { replace: true });
  };

  if (partnersLoading) {
    return <div className="space-y-4"><Skeleton className="h-40 w-full" /><Skeleton className="h-64 w-full" /></div>;
  }

  if (partners.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <Handshake className="h-8 w-8 text-muted-foreground" />
          <div>
            <p className="font-medium">No referral partners yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Add the outside companies that send you work and the fee you owe them. Their leads, jobs and fees show up here.
            </p>
          </div>
          <Button asChild variant="outline"><Link to="/settings?tab=partners">Set up referral partners</Link></Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PartnersOverview partners={partners} totals={totals} selectedId={selected?.id ?? null} onPick={pick}
        formatMoney={formatMoney} formatShortDate={formatShortDate} />
      {selected && (
        <PartnerPeriod key={selected.id} partner={selected} partners={partners} onPick={pick}
          startDate={startDate} endDate={endDate} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

function PartnersOverview({ partners, totals, selectedId, onPick, formatMoney, formatShortDate }: {
  partners: ReferralPartner[];
  totals: Record<string, { accrued: number; payable: number; paid: number; fee_count: number; last_lead_at: string | null }>;
  selectedId: string | null;
  onPick: (id: string) => void;
  formatMoney: (n: number) => string;
  formatShortDate: (v: string | Date) => string;
}) {
  const sum = (k: 'accrued' | 'payable' | 'paid') => partners.reduce((acc, p) => acc + Number(totals[p.id]?.[k] ?? 0), 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Partners</CardTitle>
            <CardDescription>
              Balances are all time, not just the selected dates. Pick a partner to see their leads for the period.
              Payouts are recorded in <Link to="/settings?tab=partners" className="underline underline-offset-2">Settings → Referral partners</Link>.
            </CardDescription>
          </div>
          <div className="flex gap-6 text-sm">
            <div><p className="text-xs text-muted-foreground">Owed now</p><p className="font-semibold tabular-nums">{formatMoney(sum('payable'))}</p></div>
            <div><p className="text-xs text-muted-foreground">Due once customers pay</p><p className="font-semibold tabular-nums">{formatMoney(sum('accrued'))}</p></div>
            <div><p className="text-xs text-muted-foreground">Paid to date</p><p className="font-semibold tabular-nums">{formatMoney(sum('paid'))}</p></div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Partner</TableHead>
              <TableHead>Deal</TableHead>
              <TableHead>Last lead</TableHead>
              <TableHead className="text-right">Fees</TableHead>
              <TableHead className="text-right">Owed now</TableHead>
              <TableHead className="text-right">Waiting on payment</TableHead>
              <TableHead className="text-right">Paid to date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {partners.map((p) => {
              const t = totals[p.id];
              return (
                <TableRow key={p.id} onClick={() => onPick(p.id)}
                  className={cn('cursor-pointer', p.id === selectedId && 'bg-muted/60', !p.is_active && 'text-muted-foreground')}>
                  <TableCell className="font-medium">
                    {p.name}{!p.is_active && <span className="ml-2 text-xs font-normal text-muted-foreground">Inactive</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{describeFeeRule(p, formatMoney)}</TableCell>
                  <TableCell className="whitespace-nowrap">{t?.last_lead_at ? formatShortDate(t.last_lead_at) : '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{t?.fee_count ?? 0}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{formatMoney(Number(t?.payable ?? 0))}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMoney(Number(t?.accrued ?? 0))}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMoney(Number(t?.paid ?? 0))}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------------- */

function PartnerPeriod({ partner, partners, onPick, startDate, endDate }: {
  partner: ReferralPartner;
  partners: ReferralPartner[];
  onPick: (id: string) => void;
  startDate: Date;
  endDate: Date;
}) {
  const { formatMoney, formatShortDate } = useCompanyLocale();
  const { data: company } = useCompanyProfile();
  const tax = useCompanyTax();
  const taxLabel = tax.enabled ? tax.label : '';

  const startIso = startDate.toISOString();
  const endIso = endDate.toISOString();
  const { data: leads = [], isLoading, error } = usePartnerStatementLeads(partner, startIso, endIso);
  const { data: totals } = useReferralPartnerTotals();
  const { data: payouts = [] } = useReferralPayouts(partner.id);

  const statement = useMemo(() => buildPartnerStatement(partner, leads), [partner, leads]);
  const t = statement.totals;
  const account = totals?.[partner.id];
  const lastPayout = payouts[0];
  const pct = isPercentBasis(partner.fee_basis);
  const costs = showsCosts(partner.fee_basis);
  const periodLabel = `${formatShortDate(startDate)} to ${formatShortDate(endDate)}`;
  const fileStem = `${partner.name.replace(/[^\w-]+/g, '_')}_statement_${startIso.slice(0, 10)}_to_${endIso.slice(0, 10)}`;

  const download = () => {
    const html = generatePartnerStatementHtml(statement, {
      companyName: company?.trading_name || company?.name || 'Our company',
      logoUrl: company?.logo_url ?? null,
      primaryColor: company?.primary_color ?? null,
      periodLabel,
      generatedLabel: formatShortDate(new Date()),
      taxLabel,
      taxRate: tax.enabled ? tax.rate : undefined,
      money: formatMoney,
      formatDate: formatShortDate,
      account: {
        owedNow: Number(account?.payable ?? 0),
        paidToDate: Number(account?.paid ?? 0),
        lastPayout: lastPayout ? `${formatMoney(Number(lastPayout.amount))} on ${formatShortDate(lastPayout.paid_on)}` : null,
      },
    });
    downloadHtmlReport(html, `${fileStem}.html`);
    toast.success(`Statement for ${partner.name} downloaded. Open it to print or save as PDF.`);
  };

  const exportCsv = () => {
    const blob = new Blob([partnerStatementCsv(statement, formatShortDate)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileStem}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const busy = isLoading || !!error;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Select value={partner.id} onValueChange={onPick}>
            <SelectTrigger className="h-9 w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.is_active ? '' : ' (inactive)'}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">{describeFeeRule(partner, formatMoney)}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={busy}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />Export CSV
          </Button>
          <Button size="sm" onClick={download} disabled={busy}>
            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Download statement
          </Button>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-destructive">Could not load the statement: {error instanceof Error ? error.message : 'unknown error'}</p>
      ) : isLoading ? (
        <div className="space-y-2"><Skeleton className="h-24 w-full" /><Skeleton className="h-48 w-full" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <MetricCard title="Leads sent" value={t.leads} icon={Users} />
            <MetricCard title="Jobs completed" value={t.completed} icon={CheckCircle2} />
            <MetricCard title="Booked / in progress" value={t.booked} icon={CalendarClock} />
            <MetricCard title="Quoted / being arranged" value={t.quoted} icon={FileText} />
            <MetricCard title={`Sales${taxLabel ? ` (inc ${taxLabel})` : ''}`} value={formatMoney(t.sales)} icon={Briefcase} />
            {pct && partner.fee_basis !== 'percent_revenue'
              ? <MetricCard title="Profit" value={formatMoney(t.base)} icon={PiggyBank} tooltip="The figure the partner's share is taken from." />
              : <MetricCard title={`Sales${taxLabel ? ` (ex ${taxLabel})` : ''}`} value={formatMoney(t.salesExTax)} icon={PiggyBank} />}
            {tax.enabled ? (
              <>
                <MetricCard title={`${partner.name}'s share (ex ${taxLabel})`} value={formatMoney(t.share)} icon={Handshake} valueColor="text-primary"
                  tooltip="Fees are worked out before tax. Pay this if the partner is not registered." />
                <MetricCard title={`${partner.name}'s share (inc ${taxLabel})`} value={formatMoney(shareIncTax(t.share, tax.rate))} icon={Handshake} valueColor="text-primary"
                  tooltip={`The share plus ${tax.rate}% ${taxLabel}. Pay this if the partner is ${taxLabel}-registered and invoices with tax.`} />
              </>
            ) : (
              <MetricCard title={`${partner.name}'s share`} value={formatMoney(t.share)} icon={Handshake} valueColor="text-primary" />
            )}
            <MetricCard title="Owed now (all time)" value={formatMoney(Number(account?.payable ?? 0))} icon={Wallet} valueColor="text-primary"
              tooltip={tax.enabled ? `Ex ${taxLabel}.` : undefined} />
          </div>
          <p className="text-xs text-muted-foreground">
            This period: {formatMoney(t.paid)} paid, {formatMoney(t.owed)} owed
            {t.waiting > 0 ? `, ${formatMoney(t.waiting)} due once customers pay` : ''}.
            {lastPayout ? ` Last payment ${formatMoney(Number(lastPayout.amount))} on ${formatShortDate(lastPayout.paid_on)}.` : ''}
          </p>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sent</TableHead>
                    <TableHead>Ref</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead className="text-right">Sale</TableHead>
                    {costs && <TableHead className="text-right">Costs</TableHead>}
                    {pct && <TableHead className="text-right">{partner.fee_basis === 'percent_revenue' ? 'Sale ex tax' : 'Profit'}</TableHead>}
                    <TableHead className="text-right">Fee</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No leads from {partner.name} between {periodLabel}.</TableCell></TableRow>
                  ) : statement.rows.map((r) => (
                    <TableRow key={r.leadNumber} className={r.outcome === 'completed' ? '' : 'text-muted-foreground'}>
                      <TableCell className="whitespace-nowrap">{formatShortDate(r.sentOn)}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.jobNumber ?? r.leadNumber}</TableCell>
                      <TableCell>{r.customer}</TableCell>
                      <TableCell>{outcomeText(r, formatShortDate)}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.saleIncTax != null ? formatMoney(r.saleIncTax) : '—'}</TableCell>
                      {costs && <TableCell className="text-right tabular-nums">{r.costs != null ? formatMoney(r.costs) : '—'}</TableCell>}
                      {pct && <TableCell className="text-right tabular-nums">{r.base != null ? formatMoney(r.base) : '—'}</TableCell>}
                      <TableCell className="text-right font-medium tabular-nums">{r.fee != null ? formatMoney(r.fee) : '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{feeStatusLabel(r, formatShortDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
